import React from 'react';
import styled, { css, keyframes } from 'styled-components';
import { Calendar, Store, X } from 'lucide-react';
import { Row } from 'styled/alignment/Row';
import { FullyCentered } from 'styled/alignment/Center';
import colors from 'theme/colors';
import breakpoints from 'theme/breakpoints';
import EventCardV2 from 'modules/events/card/EventCardV2';
import EstablishmentCardV2 from 'modules/establishment/card/EstablishmentCardV2';
import EventCardSkeleton from 'components/commons/skeletons/EventCardSkeleton';
import { getCategoryIcon } from 'modules/events/categories/categoryIconMap';

export default function SearchOverlayResults({
    featuredItems,
    results,
    hasSearched,
    isSearching,
    tags,
    activeTag,
    searchTerm,
    isEstablishment,
    onTagClick,
    onCardClick,
    onVerTodos,
    onClose,
}) {
    const sectionTitle = isEstablishment ? 'Estabelecimentos' : 'Eventos';
    const featuredTitle = isEstablishment ? 'Estabelecimentos em destaque' : 'Eventos em destaque';

    const displayItems = hasSearched ? results.slice(0, 8) : featuredItems.slice(0, 8);

    return (
        <Container>
            <TwoColumnLayout>
                {/* Left: Categories */}
                <CategoriesColumn>
                    <SectionLabel>Explore as categorias</SectionLabel>
                    <ChipsColumn>
                        {tags === null ? (
                            [1,2,3,4,5,6].map(i => <ChipSkeleton key={i} />)
                        ) : (tags || []).slice(0, 12).map(tag => (
                            <CompactChip
                                key={tag.id}
                                $selected={activeTag === tag.name}
                                onClick={() => onTagClick(tag.name)}
                            >
                                <ChipIcon tag={tag} selected={activeTag === tag.name} />
                                <span>{tag.name}</span>
                            </CompactChip>
                        ))}
                    </ChipsColumn>
                </CategoriesColumn>

                {/* Right: Cards */}
                <CardsColumn>
                    {isSearching ? (
                        <>
                            <SectionHeader>
                                <SectionLabel>{sectionTitle}</SectionLabel>
                            </SectionHeader>
                            <CardsGrid>
                                {[...Array(4)].map((_, i) => <EventCardSkeleton key={i} />)}
                            </CardsGrid>
                        </>
                    ) : hasSearched ? (
                        <>
                            <SectionHeader>
                                <SectionLabel>{sectionTitle}</SectionLabel>
                                <HeaderActions>
                                    {results.length > 0 && (
                                        <VerTodosLink onClick={onVerTodos}>Ver todos →</VerTodosLink>
                                    )}
                                    <CloseButton onClick={onClose}>
                                        <X size={14} />
                                        <span>Fechar</span>
                                    </CloseButton>
                                </HeaderActions>
                            </SectionHeader>
                            {results.length === 0 ? (
                                <EmptyState>
                                    {isEstablishment
                                        ? <Store size={36} color={colors.nightLight} />
                                        : <Calendar size={36} color={colors.nightLight} />
                                    }
                                    <EmptyTitle>
                                        Nenhum resultado para{searchTerm ? ` "${searchTerm}"` : ' essa categoria'}
                                    </EmptyTitle>
                                    <EmptySubtitle>Tente outros termos ou explore as categorias</EmptySubtitle>
                                </EmptyState>
                            ) : (
                                <CardsGrid>
                                    {displayItems.map((item, index) => (
                                        <AnimatedCard key={item.slug} $index={index}>
                                            {isEstablishment ? (
                                                <EstablishmentCardV2
                                                    data={item}
                                                    onCardClick={() => onCardClick(item)}
                                                />
                                            ) : (
                                                <EventCardV2
                                                    data={item}
                                                    onEventClick={() => onCardClick(item)}
                                                />
                                            )}
                                        </AnimatedCard>
                                    ))}
                                </CardsGrid>
                            )}
                        </>
                    ) : (
                        /* Initial state: featured items */
                        <>
                            <SectionHeader>
                                <SectionLabel>{featuredTitle}</SectionLabel>
                                <HeaderActions>
                                    <VerTodosLink onClick={onVerTodos}>Ver todos →</VerTodosLink>
                                    <CloseButton onClick={onClose}>
                                        <X size={14} />
                                        <span>Fechar</span>
                                    </CloseButton>
                                </HeaderActions>
                            </SectionHeader>
                            {featuredItems.length === 0 ? (
                                <EmptyState>
                                    {isEstablishment
                                        ? <Store size={36} color={colors.nightLight} />
                                        : <Calendar size={36} color={colors.nightLight} />
                                    }
                                    <EmptyTitle>Nenhum destaque disponível</EmptyTitle>
                                </EmptyState>
                            ) : (
                                <CardsGrid>
                                    {displayItems.map((item, index) => (
                                        <AnimatedCard key={item.slug} $index={index}>
                                            {isEstablishment ? (
                                                <EstablishmentCardV2
                                                    data={item}
                                                    onCardClick={() => onCardClick(item)}
                                                />
                                            ) : (
                                                <EventCardV2
                                                    data={item}
                                                    onEventClick={() => onCardClick(item)}
                                                />
                                            )}
                                        </AnimatedCard>
                                    ))}
                                </CardsGrid>
                            )}
                        </>
                    )}
                </CardsColumn>
            </TwoColumnLayout>
        </Container>
    );
}

function ChipIcon({ tag, selected }) {
    const Icon = getCategoryIcon(tag?.icon);
    return <Icon size={14} color={selected ? `var(--home-accent, ${colors.occasionalPurple})` : colors.nightLight} />;
}

/* ===== Layout ===== */

const Container = styled.div`
    width: 100%;
    padding: 16px;
    overflow-x: hidden;

    @media screen and (min-width: ${breakpoints.lg}) {
        padding: 20px 32px 24px;
        max-width: 1100px;
        margin: 0 auto;
        overflow-x: unset;
    }
`;

const TwoColumnLayout = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;

    @media screen and (min-width: ${breakpoints.lg}) {
        flex-direction: row;
        align-items: flex-start;
        gap: 32px;
    }
`;

const CategoriesColumn = styled.div`
    flex-shrink: 0;

    @media screen and (min-width: ${breakpoints.lg}) {
        width: 160px;
    }
`;

const CardsColumn = styled.div`
    flex: 1;
    min-width: 0;
`;

/* ===== Categories ===== */

const ChipsColumn = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    overflow-x: auto;
    gap: 8px;
    padding-bottom: 4px;
    -webkit-overflow-scrolling: touch;

    @media screen and (min-width: ${breakpoints.lg}) {
        flex-direction: column;
        flex-wrap: nowrap;
        overflow-x: unset;
        gap: 6px;
    }
`;

const CompactChip = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    border-radius: 20px;
    border: 1px solid ${({ $selected }) => $selected ? `var(--home-accent, ${colors.occasionalPurple})` : `${colors.night}20`};
    background: ${({ $selected }) => $selected ? `var(--home-accent-12, ${colors.occasionalPurple}12)` : 'transparent'};
    color: ${({ $selected }) => $selected ? `var(--home-accent, ${colors.occasionalPurple})` : colors.nightLight};
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    white-space: nowrap;
    flex-shrink: 0;
    transition: all 0.15s ease;

    &:hover {
        border-color: var(--home-accent, ${colors.occasionalPurple});
        background: var(--home-accent-08, ${colors.occasionalPurple}08);
        color: var(--home-accent, ${colors.occasionalPurple});
    }
`;

const ChipSkeleton = styled.div`
    width: 80px;
    height: 30px;
    border-radius: 20px;
    background: ${colors.neutralDark};
    flex-shrink: 0;
    animation: shimmer 1.4s infinite;

    @keyframes shimmer {
        0%, 100% { opacity: 0.5; }
        50% { opacity: 1; }
    }
`;

/* ===== Section headers ===== */

const SectionLabel = styled.h3`
    font-size: 13px;
    font-weight: 600;
    color: ${colors.nightDark};
    margin: 0 0 12px 0;
    white-space: nowrap;

    @media screen and (min-width: ${breakpoints.lg}) {
        font-size: 14px;
    }
`;

const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;

    ${SectionLabel} {
        margin-bottom: 0;
    }
`;

const HeaderActions = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    flex-shrink: 0;
`;

const VerTodosLink = styled.span`
    font-size: 12px;
    font-weight: 600;
    color: var(--home-accent, ${colors.occasionalPurple});
    cursor: pointer;
    white-space: nowrap;
    transition: opacity 0.15s ease;

    &:hover { opacity: 0.75; }
`;

const CloseButton = styled.button`
    display: flex;
    align-items: center;
    gap: 4px;
    background: none;
    border: 1px solid ${colors.night}25;
    border-radius: 8px;
    padding: 4px 8px;
    cursor: pointer;
    color: ${colors.night};
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;
    transition: all 0.15s ease;

    &:hover {
        background: ${colors.night}08;
        border-color: ${colors.night}50;
    }
`;

/* ===== Cards ===== */

const CardsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(1, 1fr);
    gap: 10px;

    @media screen and (min-width: ${breakpoints.lg}) {
        grid-template-columns: repeat(4, minmax(0, 1fr));
        gap: 12px;
    }
`;

const fadeInUp = keyframes`
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
`;

const AnimatedCard = styled.div`
    animation: ${fadeInUp} 0.3s cubic-bezier(0.22, 1, 0.36, 1) both;
    animation-delay: ${({ $index }) => Math.min($index * 35, 180)}ms;
    min-width: 0;
    width: 100%;
`;

/* ===== Empty state ===== */

const EmptyState = styled(FullyCentered)`
    flex-direction: column;
    gap: 10px;
    padding: 32px 0;
`;

const EmptyTitle = styled.p`
    font-size: 14px;
    font-weight: 600;
    color: ${colors.night};
    margin: 0;
    text-align: center;
`;

const EmptySubtitle = styled.p`
    font-size: 12px;
    color: ${colors.nightLight};
    margin: 0;
    text-align: center;
`;
